/**
 * One dial: how far actual is toward the selected plan.
 * Stack: KPI name → actual → graph → plan immediately under the arc.
 * Arc color is actual status (RYG).
 */

import React from 'react';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import { KpiTileShell } from './KpiTileShell';
import { ANNUAL_RING, fmtKpiWithUnit, gaugeFill, progressToward, type KpiTile } from './types';

interface KpiGaugeCardProps {
  tile: KpiTile;
  t: (key: string) => string;
  onHide?: () => void;
}

const VALUE_CLASS = 'text-sm font-bold leading-none text-center';

export const KpiGaugeCard: React.FC<KpiGaugeCardProps> = ({ tile, t, onHide }) => {
  const pct = tile.redacted ? null : progressToward(tile.actual, tile.primaryTarget, tile.direction);
  const statusFill = tile.color ? gaugeFill(tile.color) : 'var(--color-primary-text)';
  const gaugeData =
    pct !== null
      ? [{ name: t('kpi.actual') || 'Actual', value: pct, fill: statusFill }]
      : [{ name: 'empty', value: 0, fill: 'var(--color-border)' }];

  return (
    <KpiTileShell
      name={tile.kpiName}
      hideLabel={t('kpi.tileHide') || 'Hide from this tab'}
      onHide={onHide}
    >
      <p className={`${VALUE_CLASS} mb-1 shrink-0`} style={{ color: statusFill }}>
        {tile.redacted ? '—' : fmtKpiWithUnit(tile, tile.actual)}
      </p>

      {/*
        Recharts sizes radius from half the chart box, so a semicircle at the
        baseline leaves a dead band above the arc. Draw a 2×-tall chart and
        clip to the top half so the visible box is only the dial.
      */}
      <div className="group relative w-full shrink-0">
        <div className="relative w-full h-[4.75rem] overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[9.5rem]">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="52%"
                outerRadius="100%"
                startAngle={180}
                endAngle={0}
                data={gaugeData}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar
                  background={{ fill: 'var(--color-border)' }}
                  dataKey="value"
                  cornerRadius={6}
                  isAnimationActive={false}
                />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {!tile.redacted && (
          <div
            className="pointer-events-none absolute left-1/2 top-1 z-10 -translate-x-1/2 rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 py-1 text-[11px] leading-snug shadow-sm opacity-0 group-hover:opacity-100"
            role="tooltip"
          >
            <p className="whitespace-nowrap">
              <span className="text-[var(--color-secondary-text)]">{t('kpi.actual') || 'Actual'} </span>
              <span className="font-bold" style={{ color: statusFill }}>
                {fmtKpiWithUnit(tile, tile.actual)}
              </span>
            </p>
            <p className="whitespace-nowrap">
              <span className="text-[var(--color-secondary-text)]">{t('kpi.target') || 'Plan'} </span>
              <span className="font-bold" style={{ color: ANNUAL_RING }}>
                {fmtKpiWithUnit(tile, tile.primaryTarget)}
              </span>
            </p>
          </div>
        )}
      </div>
      <p className={`${VALUE_CLASS} mt-0.5 shrink-0`} style={{ color: ANNUAL_RING }}>
        {tile.redacted ? '—' : `${t('kpi.target') || 'Plan'} ${fmtKpiWithUnit(tile, tile.primaryTarget)}`}
      </p>
    </KpiTileShell>
  );
};
